import classNames from 'classnames';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import { stringify as stringifyQuery } from 'query-string';
import React, { Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { capitalizeFirstLetter } from '../../lib/utils';

import { useIndexUrlGenerator } from '../../contexts/RoutesContext';
import Link from './Link';

import styles from '../../../styles/partials/people.module.scss';

const propTypes = {
    people: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ),
    roles: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    isDark: PropTypes.bool,
};

const defaultProps = {
    people: [],
    roles: ['panelist', 'moderator', 'project', 'jury', 'producer', 'coordinator'],
    className: null,
    isDark: false,
};

const People = ({ people, roles, className, isDark }) => {
    const route = useIndexUrlGenerator('calendar');
    const everyone = people || [];

    let ids = [];
    const groups = roles.reduce((all, it) => {
        const items = everyone.filter(({ role = null }) => role === it);
        ids = ids.concat(items.map(({ id = null }) => id).filter((id) => id !== null));
        all[it] = items || []; // eslint-disable-line
        return all;
    }, {});

    const remainder = everyone.filter(({ id = null }) => id !== null && ids.indexOf(id) === -1);

    const metadata = useMemo(
        () =>
            Object.keys(groups)
                .map((groupName) => {
                    let label = null;
                    const items = groups[groupName];
                    switch (groupName) {
                        case 'panelist':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Panelist} other {Panelists}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        case 'moderator':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Moderator} other {Moderators}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        case 'project':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Project member} other {Project members}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        case 'jury':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Jury} other {Juries}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        case 'producer':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Producer} other {Producers}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        case 'coordinator':
                            label = (
                                <FormattedMessage
                                    defaultMessage="{count, plural, one {Coordinator} other {Coordinators}}"
                                    description="Metadata label"
                                    values={{
                                        count: items.length,
                                    }}
                                />
                            );
                            break;
                        default:
                            break;
                    }

                    return {
                        label,
                        value: items.map(({ id, name }) => ({
                            id,
                            label: name,
                        })),
                        // query: null
                    };
                })
                .concat([
                    {
                        label: (
                            <FormattedMessage
                                defaultMessage="Also with"
                                description="Metadata label"
                            />
                        ),
                        value: remainder.map(({ id, name: label }) => ({
                            id,
                            label,
                        })),
                    },
                ])
                .map(({ label, value, query = null }) => {
                    const multiple = isArray(value);
                    return value !== null && (!multiple || value.length > 0)
                        ? {
                              label,
                              values: (multiple ? value : [value])
                                  .filter((it) => it !== null)
                                  .map((it) => {
                                      const {
                                          id,
                                          label: valueLabel,
                                          url = null,
                                      } = isString(it) ? { label: it } : it;
                                      return {
                                          label: valueLabel,
                                          url:
                                              url ||
                                              (query !== null // TODO: fix this route for specific edition
                                                  ? `${route('index')}?${stringifyQuery(
                                                        {
                                                            [query]: multiple ? [id] : id,
                                                        },
                                                        { arrayFormat: 'bracket' },
                                                    )}`
                                                  : null),
                                          external: url !== null,
                                      };
                                  }),
                          }
                        : null;
                })
                .filter((it) => it !== null),
        [people],
    );

    return (
        <dl
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            <div className={styles.items}>
                {metadata.map(({ label, values }) => (
                    <div key={label} className={styles.item}>
                        <dt>{label}</dt>
                        <dd>
                            {values.map(
                                (
                                    { label: valueLabel, url, external = false, type = null },
                                    index,
                                ) => (
                                    <Fragment key={`${valueLabel}-${index + 1}`}>
                                        {url !== null ? (
                                            <Link href={url} external={external}>
                                                {type === 'language' && index === 0
                                                    ? capitalizeFirstLetter(valueLabel)
                                                    : valueLabel}
                                            </Link>
                                        ) : (
                                            valueLabel
                                        )}
                                        {values.length > 0 && index !== values.length - 1 && ', '}
                                    </Fragment>
                                ),
                            )}
                        </dd>
                    </div>
                ))}
            </div>
        </dl>
    );
};
People.propTypes = propTypes;
People.defaultProps = defaultProps;

export default People;
